// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { CommonHeadersJson } from '../api/constants';

/**
 * Credentialed fetch function matching the upstream `EamcChatbotProps.authFetcher` contract.
 *
 * Returns the raw `Response` unmodified — no JSON parsing, no reject-on-non-OK.
 * The upstream component owns response interpretation.
 *
 * Note: does NOT reuse `fetcher` from `src/api/utils.ts` because that function
 * parses JSON and rejects on non-OK — incompatible with the Response-returning
 * contract required by the upstream component.
 *
 * Normalises any `HeadersInit` shape to a plain `Record<string, string>` before
 * merging with `CommonHeadersJson`. `RequestInit.headers` accepts three forms:
 *   1. Plain object  `{ 'X-Custom': 'value' }`
 *   2. `Headers` instance — spreading an instance yields `{}` because its
 *      entries are not own-enumerable properties.
 *   3. String-tuple array  `[['X-Custom', 'value']]`
 *
 * Spreading case 2 or 3 directly silently drops all caller-supplied headers.
 */
function toHeadersRecord(h?: HeadersInit): Record<string, string> {
  if (!h) {
    return {};
  }
  if (h instanceof Headers) {
    return Object.fromEntries(h.entries());
  }
  if (Array.isArray(h)) {
    return Object.fromEntries(h as string[][]);
  }
  return h;
}

export const chatbotAuthFetcher = (
  input: RequestInfo,
  init?: RequestInit,
): Promise<Response> =>
  fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...CommonHeadersJson, ...toHeadersRecord(init?.headers) },
  });
